import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { ChevronRight } from 'blaze-ui/icons/24'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Button } from 'blaze-ui/Button'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { LinkExternal } from 'blaze-ui/icons'
import { AlertTriangle } from 'blaze-ui/icons/20/AlertTriangle'
import { UserProfileAdd2 } from 'blaze-ui/icons/20/UserProfileAdd2'
import { Spinner } from 'common/Spinner/Spinner'
import { Check } from 'blaze-ui/icons/16/Check'
import Styles from './ChannelList.module.scss'

export type ChannelItemState =
  | 'connected'
  | 'connected-disabled'
  | 'unconnected'
  | 'failed'
  | 'warning'
  | 'posted'
  | 'posted-expired'
  | 'post-now'
  | 'processing'

const LABEL_OVERRIDES: Partial<Record<ExternalIntegrationEnum, string>> = {
  [ExternalIntegrationEnum.X]: 'X/Twitter',
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 'Google Business',
}

interface ChannelListItemProps {
  provider: ExternalIntegrationEnum
  accountName?: string
  state: ChannelItemState
  isSelected: boolean
  postUrl?: string
  expiredTooltip?: string
  onSelect: () => void
  onToggle: (isChecked: boolean) => void
  onConnect: () => void
  onRetry: () => void
  onPostNow: () => void
}

export const ChannelListItem = ({
  provider,
  accountName,
  state,
  isSelected,
  postUrl,
  expiredTooltip,
  onSelect,
  onToggle,
  onConnect,
  onRetry,
  onPostNow,
}: ChannelListItemProps) => {
  const displayName = accountName || LABEL_OVERRIDES[provider] || ProviderToName[provider]
  const isInactive = state === 'connected-disabled'
  const isConnectedState = state === 'connected' || state === 'connected-disabled'
  const showToggle = isConnectedState || state === 'warning'
  const isToggleChecked = state === 'connected' || state === 'warning'

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <div
      className={cx(Styles.channelItem, {
        [Styles.isSelected]: isSelected,
        [Styles.isInactive]: isInactive,
      })}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
    >
      <div className={Styles.channelInfo}>
        <span className={Styles.platformIcon}>
          <PlatformIcon platform={provider} size={16} />
        </span>
        <Text variant="secondary" lineClamp={1} className={Styles.accountName}>
          {displayName}
        </Text>
      </div>

      <div className={Styles.actions} onClick={handleActionClick}>
        {state === 'failed' && (
          <span data-testid="channel-item-failed-indicator" className={cx(Styles.indicator, Styles.indicatorFailed)}>
            <AlertTriangle size={12} />
          </span>
        )}
        {state === 'warning' && (
          <Tooltip label="Post exceeded the character limit" placement="top">
            <span
              data-testid="channel-item-warning-indicator"
              className={cx(Styles.indicator, Styles.indicatorWarning)}
            >
              <AlertTriangle size={12} />
            </span>
          </Tooltip>
        )}
        {showToggle && (
          <ToggleButton
            checked={isToggleChecked}
            name={`channel-toggle-${provider}`}
            handleChange={() => onToggle(!isToggleChecked)}
            size="small"
          />
        )}
        {state === 'posted' && (
          <span data-testid="channel-item-posted-indicator" className={Styles.postedBadge} aria-label="Posted">
            <Check size={12} />
          </span>
        )}
        {state === 'unconnected' && (
          <span
            className={cx(Styles.unconnectedIcon, {
              [Styles.hideWhenSelected]: isSelected,
            })}
            aria-label="Channel not connected"
            role="img"
          >
            <UserProfileAdd2 size={16} />
          </span>
        )}
      </div>

      {state === 'unconnected' && (
        <Button
          className={cx(Styles.hoverButton, { [Styles.showButton]: isSelected })}
          variant="secondary"
          size="xs"
          endIcon={ChevronRight}
          onPress={onConnect}
        >
          Connect
        </Button>
      )}

      {state === 'failed' && (
        <Tooltip label="Retry Connection" placement="top">
          <Button className={Styles.hoverButton} variant="secondary" size="xs" endIcon={ChevronRight} onPress={onRetry}>
            Retry
          </Button>
        </Tooltip>
      )}

      {state === 'posted' && postUrl && (
        <Tooltip label="View Post" placement="top">
          <ButtonLink
            className={Styles.hoverButton}
            to={postUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="secondary"
            size="xs"
            endIcon={LinkExternal}
          >
            View
          </ButtonLink>
        </Tooltip>
      )}

      {state === 'post-now' && (
        <Button className={Styles.hoverButton} variant="secondary" size="xs" onPress={onPostNow}>
          Post Now
        </Button>
      )}
      {state === 'processing' && <Spinner />}

      {state === 'posted-expired' && (
        <Tooltip label={expiredTooltip ?? ''} placement="top">
          <span className={Styles.expiredButtonWrapper}>
            <Button className={Styles.expiredButton} variant="secondary" size="xs" isDisabled endIcon={LinkExternal}>
              View
            </Button>
          </span>
        </Tooltip>
      )}
    </div>
  )
}
